import { useState, useMemo, useEffect } from 'react'

export type BudgetDisplayMode = 'calculated' | 'input' | 'dual-value'

export interface UseBudgetDisplayModeReturn {
  displayMode: BudgetDisplayMode
  showCalculated: boolean
  showInput: boolean
  showBoth: boolean
  setDisplayMode: (mode: BudgetDisplayMode) => void
}

export interface UseBudgetDisplayModeOptions {
  /**
   * Whether edit mode is active - triggers auto-switching display mode
   */
  isEditMode?: boolean
  /**
   * Whether the current view has sub-dimensions below
   * Used to determine display mode in edit mode
   */
  hasSubDimensions?: boolean
  /**
   * Whether this is the budgeting page
   * When false, forces display mode to 'calculated' and prevents mode changes
   */
  isBudgetingPage?: boolean
}

export const useBudgetDisplayMode = (options: UseBudgetDisplayModeOptions = {}): UseBudgetDisplayModeReturn => {
  const { isEditMode = false, hasSubDimensions = false, isBudgetingPage = true } = options
  const [displayMode, setDisplayMode] = useState<BudgetDisplayMode>('calculated')

  // Force display mode to 'calculated' if not on budgeting page
  useEffect(() => {
    if (!isBudgetingPage) {
      setDisplayMode('calculated')
    }
  }, [isBudgetingPage])

  const showCalculated = useMemo(() => {
    return displayMode === 'calculated' || displayMode === 'dual-value'
  }, [displayMode])

  const showInput = useMemo(() => {
    return displayMode === 'input' || displayMode === 'dual-value'
  }, [displayMode])

  const showBoth = useMemo(() => {
    return displayMode === 'dual-value'
  }, [displayMode])

  // Auto-set display mode when entering edit mode (only if on budgeting page)
  useEffect(() => {
    if (isEditMode && isBudgetingPage) {
      setDisplayMode(prev => {
        // Don't change if already showing input or dual-value
        if (prev === 'input' || prev === 'dual-value') {
          return prev
        }

        return hasSubDimensions ? 'dual-value' : 'input'
      })
    }
  }, [isEditMode, hasSubDimensions, isBudgetingPage])

  return {
    displayMode,
    showCalculated,
    showInput,
    showBoth,
    setDisplayMode
  }
}
